import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { INFRA_MAINTENANCE_KINDS, newId, type InfraMaintenanceKind } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import {
  buildMaintenancePayload, faultsAt, INFRA_PHOTO_ENTITY, INFRA_PHOTO_PURPOSE, infraPhotosOf, infraVersionsOf, maintenanceJobOptions, maintenanceOf, maintenanceProblem,
  useInfraMaintenance, type InfraRow, type InfraVersionRow,
} from '../../data/geo.ts'
import { rangeBounds } from '../../data/fleet.ts'
import { usePlan } from '../../data/plan.ts'
import { faultEventsOf, faultState, useFaultEvents, useFaults } from '../../data/water.ts'
import { formatLength, type GeoJsonGeometry } from '../../lib/geo.ts'
import { formatLocal, localDate } from '../../lib/time.ts'
import { IllustrationImage } from '../../photos/Illustration.tsx'
import { PhotoField, PhotoStrip } from '../../photos/PhotoField.tsx'
import { useBlobs, usePhotoDraft, type LinkRow } from '../../photos/usePhotos.ts'
import { useMerged } from '../../sync/hooks.ts'
import type { Pending } from '../../sync/merge.ts'
import { Banner, DisabledHint, Field, Pill } from '../../ui/components.tsx'
import { useAction, useWriteAccess } from '../../ui/hooks.ts'
import { usePersonNames } from '../measures/shared.tsx'
import { FoldSummary } from '../workshop/shared.tsx'

type T = (k: string, o?: Record<string, unknown>) => string
const day = (d: string) => d.split('-').reverse().join('/')
export const accuracyText = (t: T, source: string, acc: number | null, lang: string) =>
  source === 'sketch' ? t('map.sketch') : acc == null ? t('map.accUnknown') : t('map.accuracy', { m: new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(acc), source: t(`geoSource.${source}`) })
/** A section of the panel folded under a 56-px summary (M23 item 22), its contents mounted only while open. */
function Part({ title, open = false, testId, count, children }: { title: string; open?: boolean; testId: string; count?: number; children: ReactNode }) {
  const [shown, setShown] = useState(open)
  return (
    <details className="group border-t border-slate-200 pt-1 dark:border-night-line" open={open} data-testid={testId} data-count={count} onToggle={(e) => setShown((e.currentTarget as HTMLDetailsElement).open)}>
      <FoldSummary testId={`${testId}-toggle`}>{count === undefined ? title : `${title} (${count})`}</FoldSummary>
      {shown && <div className="flex flex-col gap-2 pb-2">{children}</div>}
    </details>
  )
}

/** M16-E: the map's panel on an infrastructure feature (32, GK-023; docs/M16.md E). One place for what is known about a valve, a drain or a head, all from the
 *  device's copy so it opens with no signal: what it is and how well its position is known (M6), its newest photo, what was done on it (`geo.infra_maintenance`,
 *  newest first, and "Entretien fait" for whoever holds infra.maintain), the irrigation faults raised on it with what happened to them between people (M14,
 *  M16-L1), and its positions, the one in force first. The "confirm on site before digging" line stays. */
export function InfraPanel({ item, version, versions, lang, tz, canEdit }: { item: Pending<InfraRow>; version: InfraVersionRow | undefined; versions: readonly InfraVersionRow[]; lang: string; tz: string; canEdit: boolean }) {
  const { t } = useTranslation()
  const geom: GeoJsonGeometry | undefined = version?.geom
  const retired = !!item.retired_at
  return (
    <div className="flex flex-col gap-2" data-testid="infra-panel" data-feature={item.id}>
      <h2 className="text-lg font-bold" data-testid="info-title">{item.label}{item.code ? ` · ${item.code}` : ''}</h2>
      <p className="meta">{t(`infraKind.${item.kind}`)}</p>
      {version && (
        <p className="text-sm" data-testid="info-meta">
          {accuracyText(t, version.source, version.accuracy_m, lang)} · {formatLocal(version.captured_on, tz, 'dd/MM/yyyy')}
          {version.depth_m != null && ` · ${t('map.depth', { m: new Intl.NumberFormat(lang).format(version.depth_m) })}`}
          {version.length_m != null && geom && ` · ${formatLength(version.length_m, lang)}`}
        </p>
      )}
      {item.note && <p className="text-sm">{item.note}</p>}
      <InfraPhotos featureId={item.id} retired={retired || !!item._pending} />
      <Banner tone="warn">{t('map.digWarning')}</Banner>
      <Maintenance item={item} tz={tz} />
      <StationFaults featureId={item.id} tz={tz} />
      <Positions versions={infraVersionsOf(versions, item.id)} lang={lang} tz={tz} />
      {canEdit && <Link to="/map/infra/$featureId" params={{ featureId: item.id }} className="btn btn-primary !px-4 self-start" data-testid="info-edit">{t('map.editInfra')}</Link>}
    </div>
  )
}

/** The feature's photo (M17's portrait pattern: the newest wins, nothing is deleted): the newest large, read from this device's copy, the cache or storage
 *  (`IllustrationImage`), the older ones under a fold; whoever may take photos (attachment.add: never the shared workstation) takes a new one, registered at once
 *  on the feature (entity 'geo.infra_feature', purpose 'photo') and sent with the next sync. Used by the map's panel and by the feature's edit page. */
export function InfraPhotos({ featureId, retired, title }: { featureId: string; retired: boolean; /** a heading over it (the edit page); none on the map's panel */ title?: string }) {
  const { t } = useTranslation()
  const access = useWriteAccess('attachment.add')
  const links = useMerged<LinkRow>('attachment_link', 'core.attachment_link')
  const blobs = useBlobs()
  const photos = useMemo(() => infraPhotosOf(links ?? [], featureId), [links, featureId])
  const entity = useMemo(() => ({ entity_type: INFRA_PHOTO_ENTITY, entity_id: featureId, purpose: INFRA_PHOTO_PURPOSE }), [featureId])
  const draft = usePhotoDraft(entity)
  const newest = photos[0]
  const local = newest ? blobs.get(newest.sha256) : undefined
  const canTake = access.permitted && !retired
  if (!newest && !canTake) return null
  return (
    <div className="flex flex-col gap-2" data-testid="infra-photos" data-count={photos.length} data-newest={newest?.sha256 ?? ''}>
      {title && <h2 className="text-lg font-bold">{title}</h2>}
      {newest && <IllustrationImage link={newest} local={local?.blob} alt={t('map.infraPanel.photoAlt')} className="aspect-[4/3] w-full max-w-md rounded-2xl border border-slate-200 object-cover dark:border-night-line" testId="infra-photo" />}
      {local && <p className="meta" data-testid="infra-photo-waiting">{t('map.infraPanel.photoWaiting')}</p>}
      {photos.length > 1 && (
        <details className="group" data-testid="infra-photos-older">
          <FoldSummary testId="infra-photos-older-toggle">{t('map.infraPanel.photosOlder', { count: photos.length - 1 })}</FoldSummary>
          <PhotoStrip entityId={featureId} />
        </details>
      )}
      {canTake && <div className="max-w-md"><PhotoField draft={draft} disabled={!access.allowed} label={newest ? t('map.infraPanel.photoChange') : t('map.infraPanel.photoTake')} /></div>}
      {canTake && <p className="meta">{t('map.infraPanel.photoHelp')}</p>}
    </div>
  )
}

/** What was done on the feature, newest first, then "Entretien fait". */
function Maintenance({ item, tz }: { item: Pending<InfraRow>; tz: string }) {
  const { t, i18n } = useTranslation()
  const rows = useInfraMaintenance()
  const nameOf = usePersonNames()
  const { plan } = usePlan(i18n.language)
  const access = useWriteAccess('infra.maintain')
  const list = useMemo(() => maintenanceOf(rows ?? [], item.id), [rows, item.id])
  const canRecord = access.permitted && !item.retired_at && !item._pending
  return (
    <Part title={t('map.infraPanel.maintTitle')} count={list.length} open={list.length > 0 || canRecord} testId="infra-maintenance">
      {list.length === 0 && <p className="meta" data-testid="infra-maint-none">{t('map.infraPanel.maintNone')}</p>}
      {list.length > 0 && (
        <ul className="flex flex-col gap-2">
          {list.map((m) => {
            const job = m.job_id ? plan.byId.get(m.job_id) : undefined
            const who = nameOf(m.actor_person_id)
            return (
              <li key={m.id} className="flex flex-col gap-1 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid="infra-maint-row" data-id={m.id} data-kind={m.kind} data-pending={m._pending ? '1' : '0'}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{day(m.done_on)} · {t(`map.infraPanel.maintKind.${m.kind}`)}</span>
                  {m._pending && <Pill tone="amber">{t('map.infraPanel.maintPending')}</Pill>}
                </div>
                {m.note && <p data-testid="infra-maint-note">{m.note}</p>}
                {(who || m.work_order_id) && <p className="meta">{[who ? t('measures.by', { who }) : null, m.work_order_id ? t('map.infraPanel.maintWorkOrder') : null].filter(Boolean).join(' · ')}</p>}
                {m.job_id && (
                  <Link to="/plan/job/$jobId" params={{ jobId: m.job_id }} className="btn !px-4 self-start" data-testid="infra-maint-job">{t('map.infraPanel.maintJob', { title: job?.title_text ?? t('map.infraPanel.maintJobUnknown') })}</Link>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {canRecord && <MaintenanceForm item={item} tz={tz} jobs={plan.jobs} />}
      {!access.permitted && !item.retired_at && <p className="meta" data-testid="infra-maint-readonly">{t('map.infraPanel.maintReadOnly')}</p>}
    </Part>
  )
}

/** "Entretien fait" (a field FACT, infra.record_maintenance@1: offline-first, shown at once in the list above, pending, until the server's row replaces it).
 *  What was done as cards, the day (today unless the person says otherwise), a note, and the job it was done under when there is one that day. */
function MaintenanceForm({ item, tz, jobs }: { item: Pending<InfraRow>; tz: string; jobs: ReturnType<typeof usePlan>['plan']['jobs'] }) {
  const { t } = useTranslation()
  const { send, personId } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('infra.maintain')
  const { busy, error, run } = useAction()
  const today = localDate(new Date(), tz)
  const id = useRef(newId())
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<InfraMaintenanceKind | ''>('')
  const [doneOn, setDoneOn] = useState(today)
  const [note, setNote] = useState('')
  const [jobId, setJobId] = useState('')
  const [sent, setSent] = useState<'online' | 'offline' | null>(null)
  const options = useMemo(() => maintenanceJobOptions(jobs, doneOn, item.area_id, personId), [jobs, doneOn, item.area_id, personId])
  const problem = maintenanceProblem({ kind, doneOn, note }, today)
  const reset = () => { id.current = newId(); setKind(''); setDoneOn(localDate(new Date(), tz)); setNote(''); setJobId(''); setSent(null) }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problem || !access.allowed || !kind) return
    void run(async () => {
      await send('infra.record_maintenance@1', buildMaintenancePayload(id.current, item.id, { kind, doneOn, note, jobId: options.some((j) => j.id === jobId) ? jobId : null }))
      setSent(engine.online === false || (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : 'online')
    })
  }
  if (sent) {
    return (
      <div className="flex flex-col gap-2" data-testid="infra-maint-sent" data-state={sent}>
        <Banner tone={sent === 'offline' ? 'warn' : 'ok'}><span className="font-semibold">{t(sent === 'offline' ? 'map.infraPanel.form.savedOffline' : 'map.infraPanel.form.saved')}</span></Banner>
        <button type="button" className="btn self-start" onClick={reset} data-testid="infra-maint-another">{t('map.infraPanel.form.another')}</button>
      </div>
    )
  }
  if (!open) return <button type="button" className="btn btn-primary self-start" onClick={() => setOpen(true)} data-testid="infra-maint-open">{t('map.infraPanel.form.title')}</button>
  const problemKey = problem === 'kind' ? 'kind' : problem === 'date' ? 'date' : problem === 'future' ? 'future' : problem === 'past' ? 'past' : 'noteLong'
  return (
    <form className="flex flex-col gap-3" onSubmit={submit} data-testid="infra-maint-form">
      <h3 className="text-base font-bold">{t('map.infraPanel.form.title')}</h3>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold">{t('map.infraPanel.form.kind')}</legend>
        <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('map.infraPanel.form.kind')}>
          {INFRA_MAINTENANCE_KINDS.map((k) => (
            <button key={k} type="button" className={`btn ${kind === k ? 'btn-primary' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)} data-testid={`infra-maint-kind-${k}`}>{t(`map.infraPanel.maintKind.${k}`)}</button>
          ))}
        </div>
      </fieldset>
      <Field label={t('map.infraPanel.form.date')} hint={t('map.infraPanel.form.dateHint')} htmlFor="infra-maint-date">
        <input id="infra-maint-date" className="input" type="date" max={today} min="2000-01-01" value={doneOn} onChange={(e) => setDoneOn(e.target.value)} data-testid="infra-maint-date" />
      </Field>
      {options.length > 0 && (
        <Field label={t('map.infraPanel.form.job')} htmlFor="infra-maint-job">
          <select id="infra-maint-job" className="input" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="infra-maint-job-pick">
            <option value="">{t('map.infraPanel.form.jobNone')}</option>
            {options.map((j) => <option key={j.id} value={j.id}>{j.title_text}</option>)}
          </select>
        </Field>
      )}
      <Field label={t('map.infraPanel.form.note')} hint={t('map.infraPanel.form.noteHint')} htmlFor="infra-maint-note">
        <textarea id="infra-maint-note" className="input" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="infra-maint-note-input" />
      </Field>
      {error && <Banner tone="error"><span data-testid="infra-maint-error">{error}</span></Banner>}
      <button className="btn btn-primary" disabled={busy || !!problem || !access.allowed} data-testid="infra-maint-send">{busy ? t('common.saving') : t('map.infraPanel.form.send')}</button>
      <DisabledHint show={!!problem && access.allowed} testId="infra-maint-hint">{t(`map.infraPanel.form.missing.${problemKey}`)}</DisabledHint>
      <p className="meta">{t('map.infraPanel.form.rule')}</p>
    </form>
  )
}

/** The irrigation faults raised on this station, with what happened to them between people (seen, given, the job that follows); dealt with on the water page. */
function StationFaults({ featureId, tz }: { featureId: string; tz: string }) {
  const { t, i18n } = useTranslation()
  const faults = useFaults(); const events = useFaultEvents()
  const nameOf = usePersonNames()
  const { plan } = usePlan(i18n.language)
  const water = useWriteAccess('water.read')
  const list = useMemo(() => faultsAt(faults ?? [], featureId), [faults, featureId])
  if (list.length === 0) return null
  const today = localDate(new Date(), tz)
  const who = (id: string | null) => nameOf(id) ?? t('water.faults.someone')
  const when = (at: string) => formatLocal(at, tz, 'dd/MM/yyyy')
  return (
    <Part title={t('map.infraPanel.faultsTitle')} count={list.length} open={list.some((f) => !f.resolved_at)} testId="infra-faults">
      <ul className="flex flex-col gap-2">
        {list.map((f) => {
          const state = faultState(faultEventsOf(events ?? [], f.id))
          const age = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${f.observed_local_date}T00:00:00Z`)) / 86_400_000)
          const job = state.job ? plan.byId.get(state.job.jobId) : undefined
          return (
            <li key={f.id} className="flex flex-col gap-1 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid="infra-fault" data-id={f.id} data-state={f.resolved_at ? 'resolved' : 'open'}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">{t(`water.faultKind.${f.kind}`)}</span>
                {f.resolved_at ? <Pill tone="green">{t('water.faults.resolved')}</Pill> : <Pill tone={age >= 7 ? 'red' : 'amber'}>{t('water.faults.age', { count: age })}</Pill>}
              </div>
              <p>{f.description}</p>
              <p className="meta">{formatLocal(f.observed_at, tz)}{nameOf(f.actor_person_id) ? ` · ${t('measures.by', { who: nameOf(f.actor_person_id) })}` : ''}</p>
              {state.seenBy.map((s) => <p key={`seen-${s.personId ?? 'x'}`} className="meta" data-testid="infra-fault-event" data-kind="acknowledged">{t('water.faults.seenBy', { who: who(s.personId), date: when(s.at) })}</p>)}
              {state.assignee && <p className="meta" data-testid="infra-fault-event" data-kind="assigned">{t('water.faults.assignedTo', { who: who(state.assignee.personId), date: when(state.assignee.at) })}</p>}
              {state.job && <p className="meta" data-testid="infra-fault-event" data-kind="job_linked">{t('water.faults.jobLinked', { title: job?.title_text ?? t('water.faults.jobUnknown'), date: job ? day(job.scheduled_local_date) : when(state.job.at) })}</p>}
              {f.resolved_at && <p className="meta">{t('water.faults.resolvedOn', { date: when(f.resolved_at) })}{f.resolution_note ? ` · ${f.resolution_note}` : ''}</p>}
            </li>
          )
        })}
      </ul>
      {water.permitted && <Link to="/water" search={{ tab: 'faults' }} className="btn !px-4 self-start" data-testid="infra-faults-open">{t('map.infraPanel.faultsOpen')}</Link>}
    </Part>
  )
}

/** The feature's positions: the one in force first, then the older ones with the day they ended (a position is a version, never an edit: M6). */
function Positions({ versions, lang, tz }: { versions: readonly InfraVersionRow[]; lang: string; tz: string }) {
  const { t } = useTranslation()
  if (versions.length === 0) return null
  const ended = (v: InfraVersionRow) => rangeBounds(v.valid_during).to
  return (
    <Part title={t('map.infraPanel.versionsTitle')} count={versions.length} testId="infra-versions">
      <ul className="flex flex-col gap-1">
        {versions.map((v) => {
          const end = ended(v)
          return (
            <li key={v.id} className="flex flex-col" data-testid="infra-version" data-no={v.version_no} data-open={end ? '0' : '1'}>
              <span className="font-semibold">{t('map.version', { n: v.version_no })} · {formatLocal(v.captured_on, tz, 'dd/MM/yyyy')}{end ? ` · ${t('map.infraPanel.versionUntil', { date: formatLocal(end, tz, 'dd/MM/yyyy') })}` : ` · ${t('map.infraPanel.versionInForce')}`}</span>
              <span className="meta">{accuracyText(t, v.source, v.accuracy_m, lang)}{v.depth_m != null ? ` · ${t('map.depth', { m: new Intl.NumberFormat(lang).format(v.depth_m) })}` : ''}{v.note ? ` · ${v.note}` : ''}</span>
            </li>
          )
        })}
      </ul>
    </Part>
  )
}
